import React from 'react';
import { Paper, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Zoom } from '@mui/material';
import { Checkbox, Table } from 'flowbite-react';
import { useUserContext } from 'src/context/UserProvider';
import { useEffect, useState } from 'react';
import { OfflineDriveCandidateStatus } from 'types';

interface Props {
	tableHeaders: {
		id: string;
		label: string;
		align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' | undefined;
		sticky?: boolean;
	}[];
	showBulkSelectAction?: boolean;
	onSelectAllCandidates?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	page?: number; // Make this required
	count?: number; // Make this required
	rowsPerPage?: number; // Make this required
	showPagination?: boolean;
	setPage?: React.Dispatch<React.SetStateAction<number>>;
	setRowsPerPage?: React.Dispatch<React.SetStateAction<number>>;
	tableRows: {
		id: string;
		bgColor?: string;
		status?: string | OfflineDriveCandidateStatus;
		tableDataJsx: React.ReactNode;
		align?: 'center' | 'left' | 'right' | 'inherit' | 'justify';
	}[][];
}

const ListingTable: React.FC<Props> = ({
	showBulkSelectAction,
	tableHeaders,
	tableRows,
	count,
	page,
	setPage,
	rowsPerPage,
	setRowsPerPage,
	showPagination = false,
	onSelectAllCandidates,
}) => {
	const { bulkSelectIds, setBulkSelectIds } = useUserContext();
	const [isAllSelected, setIsAllSelected] = useState(false);
	const currentPageIds = tableRows?.map(row => row[0]?.id);

	useEffect(() => {
		const allCurrentPageSelected = currentPageIds?.every(id => bulkSelectIds.includes(id));
		setIsAllSelected(allCurrentPageSelected);
	}, [currentPageIds, bulkSelectIds]);

	const handleSelectAllCandidates = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			// Filter out candidates with "Hired" status
			const selectableIds = currentPageIds.filter(id => {
				const candidate = tableRows.flat().find(row => row.id === id);
				return candidate?.status !== OfflineDriveCandidateStatus.HIRED;
			});
			setBulkSelectIds(prev => [...new Set([...prev, ...selectableIds])]);
		} else {
			setBulkSelectIds(prev => prev.filter(id => !currentPageIds?.includes(id)));
		}
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage && setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage && setRowsPerPage(parseInt(event.target.value, 10));
		setPage && setPage(0);
	};

	const toggleSelection = (id: string) => {
		setBulkSelectIds(prev => (prev.includes(id) ? prev.filter(prevId => prevId !== id) : [...prev, id]));
	};

	return (
		<div>
			<Paper
				sx={{
					bg: 'white',
					width: '100%',
					overflow: 'hidden',
					borderRadius: '8px',
					isolation: 'isolate',
					boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
					'&>MuiTableContainer-root': {
						scrollbarWidth: '10px !important',
					},
				}}
			>
				<TableContainer
					sx={{
						minHeight: 440,
						'&>div>div': { display: 'none' },
						'::-webkit-scrollbar': {
							width: '2px !important',
							display: 'block !important',
							height: '2px !important',
						},
						'::-webkit-scrollbar-thumb': {
							backgroundColor: '#555770 !important',
							borderRadius: '10px !important',
						},
						'::-webkit-scrollbar-track': {
							backgroundColor: '#f5f5f5 !important',
						},

						// Non-WebKit (like Firefox)
						scrollbarWidth: 'auto !important',
					}}
				>
					<Table aria-label="sticky table">
						<TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'pre' }}>
							<TableRow
								sx={{
									'&': {
										background: '#F2F2F5',
										'[data-mode="dark"] &': {
											background: '#18222D',
										},
									},
								}}
							>
								{onSelectAllCandidates ? (
									<Zoom in={showBulkSelectAction} mountOnEnter unmountOnExit>
										<TableCell align="center" padding="checkbox">
											<Checkbox color="purplePlaced" checked={isAllSelected} onChange={handleSelectAllCandidates} />
										</TableCell>
									</Zoom>
								) : showBulkSelectAction ? (
									<Zoom in={showBulkSelectAction} mountOnEnter unmountOnExit>
										<TableCell align="center" padding="checkbox" />
									</Zoom>
								) : null}
								{tableHeaders.map((header, id) => (
									<TableCell
										className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
										key={id}
										align={header.align || 'center'}
										sx={
											header.label === 'Actions'
												? {
													position: 'sticky',
													right: 0,
													background: '#F2F2F5',
													zIndex: 2,
													'[data-mode="dark"] &': {
														background: '#18222D',
													},
												}
												: {}
										}
									>
										{header.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody
							sx={{
								background: '#FFF',
								'&': {
									'[data-mode="dark"] &': {
										background: '#18222D',
									},
								},
							}}
						>
							{tableRows.map((row, id) => {
								return (
									<React.Fragment key={id}>
										<TableRow
											hover
											role="checkbox"
											tabIndex={-1}
											sx={{
												isolation: 'isolate',
												position: 'relative',
												backgroundColor: row[0]?.status === OfflineDriveCandidateStatus.HIRED ? '#f0f0f0' : 'white',
												cursor: row[0]?.status === OfflineDriveCandidateStatus.HIRED ? 'not-allowed' : 'pointer',
												opacity: showBulkSelectAction && row[0]?.status === OfflineDriveCandidateStatus.HIRED ? 0.5 : 1,
												'& .MuiTableCell-root:last-of-type': {
													background: row?.find(cell => cell?.bgColor) ? '#f5f5f5 !important' : '',
												},
												'&:hover': {
													backgroundColor: row[0]?.status === OfflineDriveCandidateStatus.HIRED ? '#f0f0f0' : '#f5f5f5',
													cursor: 'pointer',
													'.MuiTableCell-root': {
														background: 'transparent',
													},
													'.MuiTableCell-root:last-of-type': {
														background: '#f5f5f5',
													},
												},
											}}
											onClick={e => {
												if (!showBulkSelectAction || row[0]?.status === OfflineDriveCandidateStatus.HIRED) return;
												toggleSelection(row[0]?.id);
											}}
										>
											<Zoom in={showBulkSelectAction} mountOnEnter unmountOnExit>
												<TableCell align="center" padding="checkbox">
													<Checkbox
														color="purplePlaced"
														checked={bulkSelectIds.includes(row[0]?.id)}
														disabled={row[0]?.status === OfflineDriveCandidateStatus.HIRED ? true : false}
														onClick={e => {
															e.stopPropagation();
															if (row[0]?.id) {
																toggleSelection(row[0].id);
															}
														}}
													/>
												</TableCell>
											</Zoom>
											{row.map((cell, id) => (
												<TableCell
													key={id}
													align={cell?.align || 'left'}
													sx={{
														whiteSpace: 'pre',
														position: cell.id === 'actions' ? 'sticky' : 'relative',
														right: cell.id === 'actions' ? 0 : 'auto',
														zIndex: cell.id === 'actions' ? 2 : 'auto',
														backgroundColor: row?.find(cell => cell?.bgColor) ? 'transparent' : 'white',
														'&:hover': {
															cursor: 'pointer',
														},
													}}
												>
													<>
														{row?.find(cell => cell?.bgColor)?.bgColor ? (
															<div
																className="absolute w-full h-full z-[-1] inset-0 pointer-events-none"
																style={{ backgroundColor: row?.find(cell => cell?.bgColor)?.bgColor }}
															></div>
														) : null}

														{cell.tableDataJsx}
													</>
												</TableCell>
											))}
										</TableRow>
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				{showPagination ? (
					<TablePagination
						sx={{
							'&': {
								background: '#FAFAFC',
								'[data-mode="dark"] &': {
									background: '#18222D',
								},
							},
						}}
						rowsPerPageOptions={[5, 10, 25, 100]}
						component="div"
						page={page || 0}
						count={count || 0}
						rowsPerPage={rowsPerPage || 10}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				) : null}
			</Paper>
		</div>
	);
};

export default ListingTable;
